import { memo, useCallback, useMemo, useState } from 'react'

import { LoadIndicator } from 'devextreme-react'
import {
  CrewAvatar,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { useParams } from 'react-router-dom'
import { skipToken } from '@reduxjs/toolkit/query'
import { CrewFavorite } from 'components/elements/crewFavorite/crewFavorite'
import { GetProjectForDirectChannelRequest } from '@crew/apis/project/models/getProjectForDirectChannel/request'
import { useGetProjectForDirectChannelQuery } from '@crew/apis/project/projectApis'
import {
  CrewUserPresence,
  UserPresenceState,
} from 'components/elements/crewUserPresence/crewUserPresence'
import { CrewPresenceStateIconSize } from 'components/elements/crewPresenceStateIcon/crewPresenceStateIcon'
import { useTranslation } from '@crew/modules/i18n'
import { CrewNotificationProject } from 'components/elements/crewNotificationProject/crewNotificationProject'
import { usePresenceState } from '@crew/states'

export const DirectMessageHeadPanel = memo(() => {
  const { t } = useTranslation()
  const { projectId } = useParams()

  // ユーザー詳細を取得する
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
  const getProjectForDirectChannel:
    | GetProjectForDirectChannelRequest
    | undefined = projectId
    ? {
        projectId,
      }
    : undefined
  const {
    data: getDirectChannelResult,
    isLoading: isLoadingUserDetail,
    isError: isErrorUserDetail,
  } = useGetProjectForDirectChannelQuery(
    getProjectForDirectChannel ?? skipToken
  )

  // ユーザー在席状態
  const [userPresenceState, setUserPresenceState] =
    useState<UserPresenceState>()

  // 在席状態IDから在席状態を取得
  const presenceState = usePresenceState(userPresenceState?.presenceStateId)

  // handle presence state change
  const handleUserPresenceStateChange = useCallback(
    (userPresence: UserPresenceState) => {
      setUserPresenceState(userPresence)
    },
    []
  )

  // text presence state
  const textPresenceState = useMemo(() => {
    // 在席状態名（取得できない場合は「不明」を表示）
    const presenceStateName = presenceState?.name ?? t('label.unknown')

    return !userPresenceState?.presenceStateMessage
      ? presenceStateName // メッセージがない場合：在席状態のみ表示
      : `${presenceStateName} / ${userPresenceState.presenceStateMessage}` // メッセージがある場合：「在席状態 / メッセージ」の形式で表示
  }, [presenceState?.name, t, userPresenceState?.presenceStateMessage])

  if (!getDirectChannelResult?.directChannel) {
    return null
  }

  // Get the partner of the logged in user in the current channel
  const partner = getDirectChannelResult.directChannel.user2

  if (isErrorUserDetail) {
    return null
  }
  if (isLoadingUserDetail) {
    return <LoadIndicator height="1em" width="1em" />
  }

  return (
    <>
      <div className="flex flex-row items-center gap-2">
        <div className="relative">
          <CrewAvatar
            displayName={partner.displayName}
            size={CrewAvatarSize.lg}
            imageURL={generateImageAvatarUrl(
              EntityType.DirectChannel,
              projectId
            )}
            cacheValue={partner.id + partner.version}
            showPresenceState
            textPresenceState={textPresenceState}
          />

          {/* crew user presence */}
          <div className="absolute bottom-0 right-0">
            <CrewUserPresence
              userId={partner.id}
              size={CrewPresenceStateIconSize.base}
              onChange={handleUserPresenceStateChange}
              borderVisible
              borderClass="border-2"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex flex-row items-center gap-2">
            {/* 表示名 */}
            <div
              className="font-bold text-2xl line-clamp-1 max-w-xs"
              title={partner.displayName}
            >
              {partner.displayName}
            </div>
            {/* 通知アイコン */}
            <CrewNotificationProject
              projectId={getDirectChannelResult.directChannel.id}
            />

            {/* お気に入りアイコン */}
            <CrewFavorite
              key={getDirectChannelResult.directChannel.id}
              entityType={EntityType.Project}
              entityRecordId={getDirectChannelResult?.directChannel.id}
              // TODO: directChannelFavoriteの有無で判断するのが冗長なので以下で解消予定
              // https://break-tmc.atlassian.net/browse/CREW-18574
              isFavorite={
                getDirectChannelResult.directChannel.directChannelFavorite
                  ? true
                  : false
              }
            />
          </div>

          {/* text presence state */}
          <div className="text-base crew-text-gray-4">{textPresenceState}</div>
        </div>
      </div>
    </>
  )
})
